import { Controller } from "@hotwired/stimulus"
import {
  addNode, addSeparator, addStage, conversionAfter, errorsAt, moveNode, moveStage, parseSpec,
  pruneConversions, removeNode, removeStage, setConversion, updateNode, updateStage,
} from "../lib/ai_report_tree_spec"

// 管理画面の KPI ツリーの編集(設計書 2026-10-13 §5)。定義の JSON を textarea に持ち、操作のたびに組み立て直して描き直す。
// JS が動かない・JSON が壊れているときは textarea をそのまま見せる(JSON を直接直せる)。
// 管理者が入力した文字列は textContent / value でだけ入れる(innerHTML は使わない)
// 保存済みの定義が無効(未知の指標・段0個など)でも描ける。万一描けなければ textarea に戻す
export default class extends Controller {
  static targets = ["input", "editor", "slideButton"]
  static values = { catalog: Object, errors: Array }

  connect() {
    this.spec = parseSpec(this.inputTarget.value)
    if (!this.spec) return
    this.inputTarget.hidden = true
    this.render()
  }

  // 送信の直前に textarea へ書き戻す(保存・プレビューのどのボタンでも呼ばれる)
  sync(event) {
    if (this.spec) this.inputTarget.value = JSON.stringify(this.spec)
    // 「Slideで確認」は Turbo を通さない通常の送信なので、二度押しで Slide を重ねて作らないよう押した後に無効にする。
    // 同期で disabled にすると送信値から外れるので、送信が始まった後(次のティック)にする
    const button = event?.submitter
    if (button && this.hasSlideButtonTarget && button === this.slideButtonTarget) {
      setTimeout(() => {
        button.disabled = true
        button.textContent = "作成中…"
      }, 0)
    }
  }

  change(next) {
    this.update(next)
    this.render()
  }

  // 文字の入力中は描き直さない(入力欄のフォーカスを保ち、続けて押したボタンのクリックを取りこぼさない)
  update(next) {
    this.spec = pruneConversions(next)
    this.sync()
  }

  render() {
    const root = this.editorTarget
    try {
      root.replaceChildren()
      this.errorList([]).forEach((el) => root.append(el))
      this.spec.stages.forEach((stage, i) => {
        if (i > 0) root.append(this.conversionRow(this.spec.stages[i - 1].key))
        root.append(this.stageCard(stage, i))
      })
      root.append(this.button("段を追加", "btn btn-outline btn-sm", () => this.change(addStage(this.spec))))
    } catch (error) {
      console.error(error)
      root.replaceChildren()
      this.spec = null
      this.inputTarget.hidden = false
    }
  }

  stageCard(stage, i) {
    const path = ["stages", i]
    const card = this.el("section", "rounded-box border border-base-300 p-3 space-y-2")
    const head = this.el("div", "flex flex-wrap items-end gap-2")
    head.append(
      this.textField("見出し(10文字まで)", stage.title || "", (v) => this.update(updateStage(this.spec, i, { title: v })), 10),
      this.textField("AI考察の見出し(任意・20文字まで)", stage.ai_title || "", (v) => this.update(updateStage(this.spec, i, { ai_title: v })), 20),
      this.checkbox("この段をAIに考察させる", stage.ai !== false, (v) => this.change(updateStage(this.spec, i, { ai: v }))),
      this.button("↑", "btn btn-ghost btn-xs", () => this.change(moveStage(this.spec, i, -1)), "段を上へ"),
      this.button("↓", "btn btn-ghost btn-xs", () => this.change(moveStage(this.spec, i, 1)), "段を下へ"),
      this.button("段を削除", "btn btn-ghost btn-xs text-error", () => this.change(removeStage(this.spec, i))),
    )
    card.append(head)
    ;[...this.errorList(path), ...this.errorList([...path, "key"]), ...this.errorList([...path, "title"]), ...this.errorList([...path, "ai_title"]),
      ...this.errorList([...path, "ai"]), ...this.errorList([...path, "nodes"])].forEach((el) => card.append(el))
    const list = this.el("ul", "space-y-1")
    this.nodesOf(stage).forEach((node, j) => list.append(this.nodeRow(node, i, j)))
    card.append(list, this.addNodeRow(i))
    return card
  }

  nodeRow(node, i, j) {
    const li = this.el("li", "flex flex-wrap items-center gap-2 text-sm")
    if (node.separator) {
      li.append(this.el("span", "text-base-content/60", "―― 区切り線 ――"))
    } else {
      const label = this.metricLabel(node.metric)
      li.append(this.el("span", node.child ? "ps-4" : "font-semibold", label))
      li.append(this.checkbox("子ノード", node.child === true, (v) => this.change(updateNode(this.spec, i, j, { child: v }))))
    }
    li.append(
      this.button("↑", "btn btn-ghost btn-xs", () => this.change(moveNode(this.spec, i, j, -1)), "上へ"),
      this.button("↓", "btn btn-ghost btn-xs", () => this.change(moveNode(this.spec, i, j, 1)), "下へ"),
      this.button("削除", "btn btn-ghost btn-xs text-error", () => this.change(removeNode(this.spec, i, j))),
    )
    this.errorList(["stages", i, "nodes", j]).forEach((el) => li.append(el))
    return li
  }

  addNodeRow(i) {
    const row = this.el("div", "flex flex-wrap items-center gap-2")
    const select = this.el("select", "select select-bordered select-sm")
    select.setAttribute("aria-label", "追加する指標")
    const used = new Set(this.spec.stages.flatMap((s) => this.nodesOf(s).map((n) => n.metric)))
    this.catalogValue.metrics.filter((m) => !used.has(m.key)).forEach((m) => {
      const opt = this.el("option", "", m.conditional ? `${m.label}(内訳が使えるときだけ表示)` : m.label)
      opt.value = m.key
      select.append(opt)
    })
    row.append(select,
      this.button("指標を追加", "btn btn-outline btn-xs", () => { if (select.value) this.change(addNode(this.spec, i, select.value)) }),
      this.button("区切り線を追加", "btn btn-ghost btn-xs", () => this.change(addSeparator(this.spec, i))))
    return row
  }

  conversionRow(afterKey) {
    const row = this.el("div", "flex flex-wrap items-center gap-2 ps-4 text-sm")
    row.append(this.el("span", "text-base-content/60", "↓ 段のあいだの率"))
    const select = this.el("select", "select select-bordered select-sm")
    select.setAttribute("aria-label", "段のあいだの率")
    const none = this.el("option", "", "なし(矢印だけ)")
    none.value = ""
    select.append(none)
    this.catalogValue.rates.forEach((r) => {
      const opt = this.el("option", "", r.label)
      opt.value = r.key
      select.append(opt)
    })
    const current = conversionAfter(this.spec, afterKey)
    // カタログに無い率(無効な保存済みの定義)も選ばれた状態で見せる(選び直せば消える)
    if (current && !this.catalogValue.rates.some((r) => r.key === current)) {
      const unknown = this.el("option", "", `(使えない率: ${current})`)
      unknown.value = current
      select.append(unknown)
    }
    select.value = current
    select.addEventListener("change", () => this.change(setConversion(this.spec, afterKey, select.value)))
    row.append(select)
    const conversions = Array.isArray(this.spec.conversions) ? this.spec.conversions : []
    const convIndex = conversions.findIndex((c) => c?.after === afterKey)
    if (convIndex >= 0) [...this.errorList(["conversions", convIndex]), ...this.errorList(["conversions", convIndex, "rate"])].forEach((el) => row.append(el))
    return row
  }

  nodesOf(stage) {
    return Array.isArray(stage.nodes) ? stage.nodes : []
  }

  metricLabel(key) {
    return this.catalogValue.metrics.find((m) => m.key === key)?.label || `(使えない指標: ${key})`
  }

  errorList(path) {
    return errorsAt(this.errorsValue, path).map((message) => this.el("p", "text-error text-xs", message))
  }

  textField(label, value, onChange, maxLength) {
    const wrap = this.el("label", "form-control")
    wrap.append(this.el("span", "label-text text-xs", label))
    const input = this.el("input", "input input-bordered input-sm")
    input.type = "text"
    input.value = value
    input.maxLength = maxLength
    input.addEventListener("input", () => onChange(input.value))
    wrap.append(input)
    return wrap
  }

  checkbox(label, checked, onChange) {
    const wrap = this.el("label", "label cursor-pointer gap-2 text-xs")
    const box = this.el("input", "checkbox checkbox-sm")
    box.type = "checkbox"
    box.checked = checked
    box.addEventListener("change", () => onChange(box.checked))
    wrap.append(box, this.el("span", "", label))
    return wrap
  }

  button(text, className, onClick, ariaLabel) {
    const b = this.el("button", className, text)
    b.type = "button"
    if (ariaLabel) b.setAttribute("aria-label", ariaLabel)
    b.addEventListener("click", onClick)
    return b
  }

  el(tag, className, text) {
    const e = document.createElement(tag)
    if (className) e.className = className
    if (text !== undefined) e.textContent = text
    return e
  }
}
